/**
 * Invoices that have been written but never asked for.
 *
 * A draft is money the client has not been told about. That is a reasonable
 * state to pass through and a bad state to sit in, and nothing on the dashboard
 * distinguished the two — `outstandingTotal` excludes drafts on purpose,
 * because a draft is not owed, so a generated invoice was invisible everywhere
 * that mattered.
 *
 * Cathy Torres's biweekly retainer generated on 31 August, sat as a draft for a
 * week, and the only place it appeared was a "Draft" tile on the invoices page
 * that you had to already be looking at.
 */

/** Past this, a draft has stopped being "about to be sent" and is just stuck. */
export const STALE_DRAFT_DAYS = 3

function dayOf(inv) {
  return inv?.issue_date || inv?.created_at || null
}

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

/**
 * Calendar days, not elapsed milliseconds.
 *
 * `issue_date` is a date with no time in it. Reading it as an instant and
 * subtracting makes the answer depend on the reader's offset — 31 August read
 * as local noon, measured against a UTC noon today, came out as six days west
 * of Greenwich and seven days east of it. Comparing local midnights gives the
 * number a person would get by counting on a calendar, everywhere.
 */
export function waitingDays(inv, now = new Date()) {
  const raw = dayOf(inv)
  if (!raw) return 0
  const s = String(raw)
  const started = /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)))
    : new Date(s)
  if (Number.isNaN(started.getTime())) return 0
  // Rounded, so an hour of daylight saving cannot shave a day off.
  return Math.max(0, Math.round((startOfDay(now) - startOfDay(started)) / 86400000))
}

/**
 * Drafts, longest-waiting first — which is the order they need sending in, and
 * puts the one that has been forgotten at the top rather than the newest.
 */
export function draftsAwaitingSend(invoices = [], now = new Date()) {
  return (invoices || [])
    .filter(i => String(i?.status || '') === 'draft')
    .map(i => ({ ...i, waitingDays: waitingDays(i, now) }))
    .sort((a, b) => b.waitingDays - a.waitingDays)
}

export function draftsTotal(rows = []) {
  return rows.reduce((s, i) => s + (parseFloat(i?.amount) || 0), 0)
}

/** How long it has been sitting, in words. Empty for one written today. */
export function describeWait(days) {
  if (!Number.isFinite(days) || days <= 0) return 'written today'
  if (days === 1) return 'waiting 1 day'
  return `waiting ${days} days`
}

export function hasStaleDraft(rows = []) {
  return rows.some(r => r.waitingDays >= STALE_DRAFT_DAYS)
}
